import { z } from "zod/v4"
import { StampLaneRewardId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { StampLaneRewardItem, StampLaneRewardCoin } from "./stamp-lane-reward"

export const UserStampRewardClaimSource = z.enum(["stamps", "auto"]).describe(
  "受取方法。'stamps'＝スタンプ到達による受取、'auto'＝期限後の自動付与"
)
export type UserStampRewardClaimSource = z.infer<typeof UserStampRewardClaimSource>

const UserStampRewardClaimBase = z.object({
  user_id: z.string().describe("ユーザーID（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  card_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  lane_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  threshold_stamps: z.number().describe("当該報酬に必要な累計スタンプ数。受取時点の閾値スナップショット"),
  claimed: z.boolean().describe("受取済みフラグ"),
  claimed_at: z.date().optional().describe("受取日時（JST）"),
  claim_source: UserStampRewardClaimSource,
  stamp_count_at_claim: z.number().describe("受取時点の累計スタンプ数スナップショット"),
})

const UserStampRewardClaimItemMeta = UserStampRewardClaimBase.extend({
  kind: z.literal("item").describe("報酬の種別。'item'＝アイテム付与"),
  item: StampLaneRewardItem.describe("受け取ったアイテム報酬の詳細"),
})

const UserStampRewardClaimCoinMeta = UserStampRewardClaimBase.extend({
  kind: z.literal("coin").describe("報酬の種別。'coin'＝無償POコイン付与"),
  coin: StampLaneRewardCoin.describe("受け取ったコイン報酬の詳細"),
})

export const UserStampRewardClaimDocumentMeta = z.discriminatedUnion("kind", [
  UserStampRewardClaimItemMeta,
  UserStampRewardClaimCoinMeta,
])

export type UserStampRewardClaimDocumentMeta = z.infer<typeof UserStampRewardClaimDocumentMeta>

export const UserStampRewardClaimDocumentBody = UserStampRewardClaimDocumentMeta

export type UserStampRewardClaimDocumentBody = z.infer<typeof UserStampRewardClaimDocumentBody>

export type UserStampRewardClaimDocument = UserStampRewardClaimDocumentBody & {
  id: StampLaneRewardId,
}

export class UserStampRewardClaimDocuments {

  static toDocumentData(doc: UserStampRewardClaimDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserStampRewardClaimDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const kind = data["kind"]
    const base = {
      id: StampLaneRewardId(doc.id),
      user_id: data["user_id"],
      card_id: data["card_id"],
      lane_id: data["lane_id"],
      threshold_stamps: data["threshold_stamps"],
      claimed: data["claimed"],
      claimed_at: data["claimed_at"],
      claim_source: data["claim_source"],
      stamp_count_at_claim: data["stamp_count_at_claim"],
    }

    if (kind === "item") {
      return {
        ...base,
        kind: "item",
        item: data["item"],
      }
    } else {
      return {
        ...base,
        kind: "coin",
        coin: data["coin"],
      }
    }
  }
}
